import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Tooltip,
  Link,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  People as PeopleIcon,
  Lock as LockIcon,
} from "@mui/icons-material";

import { removePlayerFromPrivateGame } from "_DatabaseFunctions/directConnect/hosts/gameFunctions";
import { doc, getDoc } from "firebase/firestore";
import { db } from "config/firebase";

interface AllowedPlayersSectionProps {
  gameInfo: {
    id: string;
    gameName: string;
    access: string;
    hostId: string;
    allowedPlayers?: { [key: string]: boolean };
  };
}

// No need for a custom RootState interface - import from types
import { RootState } from "types";

function AllowedPlayersSection({ gameInfo }: AllowedPlayersSectionProps) {
  const navigate = useNavigate();
  const [userDisplayNames, setUserDisplayNames] = useState<{ [key: string]: string }>({});
  const [removeDialog, setRemoveDialog] = useState<{
    open: boolean;
    playerId: string;
    playerName: string;
  }>({
    open: false,
    playerId: "",
    playerName: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Only show this section for private games
  if (gameInfo.access !== "Private") {
    return null;
  }

  const allowedPlayerIds = gameInfo.allowedPlayers
    ? Object.keys(gameInfo.allowedPlayers)
    : [];

  // Fetch display names for allowed players
  useEffect(() => {
    const fetchUserDisplayNames = async () => {
      const names: { [key: string]: string } = {};
      
      for (const playerId of allowedPlayerIds) {
        if (!userDisplayNames[playerId]) {
          try {
            const userDoc = await getDoc(doc(db, "users", playerId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              names[playerId] = userData.displayName || "Unknown Player";
            } else {
              names[playerId] = "Unknown Player";
            }
          } catch (error) {
            console.error("Error fetching user data:", error);
            names[playerId] = "Unknown Player";
          }
        }
      }
      
      if (Object.keys(names).length > 0) {
        setUserDisplayNames(prev => ({ ...prev, ...names }));
      }
    };

    if (allowedPlayerIds.length > 0) {
      fetchUserDisplayNames();
    }
  }, [allowedPlayerIds, userDisplayNames]);

  const handleRemoveClick = (playerId: string) => {
    const playerName = userDisplayNames[playerId] || "Unknown Player";
    setRemoveDialog({
      open: true,
      playerId,
      playerName,
    });
  };

  const handleRemoveConfirm = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const result = await removePlayerFromPrivateGame({
        gameId: gameInfo.id,
        playerId: removeDialog.playerId,
        hostId: gameInfo.hostId,
      });

      if (result.status === "SUCCESS") {
        setMessage({
          type: "success",
          text: `${removeDialog.playerName} has been removed from the game.`,
        });
      } else {
        setMessage({
          type: "error",
          text: result.message || "Failed to remove player.",
        });
      }
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.message || "An error occurred while removing the player.",
      });
    } finally {
      setLoading(false);
      setRemoveDialog({ open: false, playerId: "", playerName: "" });

      // Clear message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleRemoveCancel = () => {
    setRemoveDialog({ open: false, playerId: "", playerName: "" });
  };

  const handlePlayerNameClick = (playerId: string) => {
    navigate(`/profile/${playerId}`);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <LockIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" component="h3">
              Private Game Access
            </Typography>
            <Chip
              label={`${allowedPlayerIds.length} players`}
              size="small"
              color="primary"
              sx={{ ml: 2 }}
            />
          </Box>

          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          {allowedPlayerIds.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <PeopleIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
              <Typography variant="body2" color="textSecondary">
                No players have joined this private game yet.
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Players can join using the access code.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Players with access to this private game:
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Player Name</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allowedPlayerIds.map((playerId, index) => {
                    const playerName = userDisplayNames[playerId] || "Unknown Player";

                    return (
                      <TableRow 
                        key={playerId}
                        sx={{
                          backgroundColor: index % 2 === 0 ? "white" : "grey.50",
                          "&:hover": {
                            backgroundColor: index % 2 === 0 ? "grey.100" : "grey.200",
                          },
                        }}
                      >
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => handlePlayerNameClick(playerId)}
                            sx={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              color: "primary.main",
                              "&:hover": {
                                color: "primary.dark",
                              },
                            }}
                          >
                            {playerName}
                          </Link>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Remove player from private game">
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveClick(playerId)}
                              disabled={loading}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={removeDialog.open} onClose={handleRemoveCancel}>
        <DialogTitle>Remove Player Access</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove{" "}
            <strong>{removeDialog.playerName}</strong> from this private game?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            They will no longer be able to see or access this game. They would
            need the access code again to rejoin.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleRemoveConfirm}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {loading ? "Removing..." : "Remove Player"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AllowedPlayersSection;
